import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { ProductProvider } from './contexts/ProductContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Pages
import SplashPage from './pages/SplashPage';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import CategoryShopPage from './pages/CategoryShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import OrdersPage from './pages/OrdersPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import GalleryPage from './pages/GalleryPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ReturnsPage from './pages/ReturnsPage';
import DeleteAccountPage from './pages/DeleteAccountPage';

// Layout
import Layout from './components/layout/Layout';

import { checkProfileComplete } from './services/database';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Profile Guard (redirects to complete profile if needed)
const ProfileGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [profileComplete, setProfileComplete] = useState<boolean | null>(null);

  useEffect(() => {
    const checkProfile = async () => {
      if (currentUser) {
        const isComplete = await checkProfileComplete(currentUser.uid);
        setProfileComplete(isComplete);
      }
    };
    checkProfile();
  }, [currentUser]);

  if (profileComplete === null) {
    return <div>Loading...</div>;
  }

  if (profileComplete === false) {
    return <Navigate to="/complete-profile" replace />;
  }

  return children;
};

function AppContent() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProductProvider>
          <CartProvider>
            <Layout>
              <Routes>
                {/* Home page is the default landing page - no login required */}
                <Route path="/" element={<HomePage />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route
                  path="/complete-profile"
                  element={
                    <ProtectedRoute>
                      <CompleteProfilePage />
                    </ProtectedRoute>
                  }
                />
                {/* Public routes - no authentication required */}
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/trophies" element={<CategoryShopPage categoryFilter="Trophies" />} />
                <Route path="/medals" element={<CategoryShopPage categoryFilter="Medals" />} />
                <Route path="/plaques" element={<CategoryShopPage categoryFilter="Plaques" />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/product/:id" element={<ProductDetailPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/returns" element={<ReturnsPage />} />
                <Route path="/delete-account" element={<DeleteAccountPage />} />
                {/* Orders page - shows guest view if not logged in */}
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/profile" element={<ProfilePage />} />
              </Routes>
            </Layout>
            <ToastContainer
              position="top-center"
              autoClose={4000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </CartProvider>
        </ProductProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function App() {
  return <AppContent />;
}

export default App;
